import {Key} from 'ts-key-enum';
import {RelativeDirection} from './direction';

/**
 * Describes a navigation event within a grid.
 */
export class GridNavigationEvent {

	/**
	 *
	 * @param {RelativeDirection} direction The direction to navigate
	 * @param {number} offset A positive integer of cells to move, or -1 for
	 * 		the final cell in direction.
	 * @param {KeyboardEvent} sourceEvent The source event that triggered this event.
	 */
	constructor(
		public direction: RelativeDirection,
		public offset: number,
		public sourceEvent?: KeyboardEvent // could be expanded to include other events
	) {
	}

	/**
	 * Translates `KeyboardEvent` to `GridNavigationEvent`.
	 * @param {KeyboardEvent} event
	 * @param {number} pageSize How many cells to move for paging keys (`PageDown`/`PageUp`)
	 * @returns {GridNavigationEvent | undefined} `GridNavigationEvent` or `undefined` if the keypress
	 * 		is not supported.
	 */
	static fromKeyboardEvent(event: KeyboardEvent, pageSize: number) {
		interface ConfigMap {
			[key: string]: { direction: RelativeDirection, offset: number }
		}
		const configMap: ConfigMap = {
			[Key.ArrowLeft]: { direction: RelativeDirection.left, offset: 1 },
			[Key.ArrowDown]: { direction: RelativeDirection.down, offset: 1 },
			[Key.ArrowUp]: { direction: RelativeDirection.up, offset: 1 },
			[Key.ArrowRight]: { direction: RelativeDirection.right, offset: 1 },
			// home and end represent left and right, or up and down if ctrl key is held
			[Key.Home]: {
				direction: event.ctrlKey ? RelativeDirection.up : RelativeDirection.left,
				offset: Infinity
			},
			[Key.End]: {
				direction: event.ctrlKey ? RelativeDirection.down : RelativeDirection.right,
				offset: Infinity
			},
			[Key.PageDown]: { direction: RelativeDirection.down, offset: pageSize },
			[Key.PageUp]: { direction: RelativeDirection.up, offset: pageSize }
		};

		const key = event.key as Key;
		const config = configMap[key];
		// return undefined for unsupported keys so that callers can make control
		// flow decisions
		return config
			? new GridNavigationEvent(config.direction, config.offset, event)
			: undefined;
	}
}

